import SelectViewingGroups from "@components/auto-completes/viewing-groups";
import {
  SHDivider,
  SHLabel,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { useInputDotIndicator } from "@hooks/useInvestmentProduct";
import { FieldTypeId, HighlightType } from "@models/configuration";
import { InvestmentProductConfigurationFeature } from "@models/product/investment-product/entities/investmentProduct";
import { InvestmentProductSeriesDTO } from "@models/product/investment-product/entities/series";
import { InvestmentDataStatus } from "@models/product/investment-product/enums/status";
import { ViewingGroupSelectionDTO } from "@models/viewing-groups/entities/viewingGroups";
import { CustomLabel } from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/custom-label";
import { SeriesPath } from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/tabs/series";
import {
  getFeatureDataPath,
  getShowHideNoRoleFieldStatus,
} from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/tabs/series/utils";
import { EditProductDefaults } from "@pages/suppliers/_id/_products/investment-product/_id/edit/constants";
import { debounce, isEmpty, isEqual, isNil } from "lodash";
import { Controller, useFormContext } from "react-hook-form";
import { SHNumericFieldV2 } from "@components/design-systems/sh-numeric-field-v2";
import { toEditManagerNativeValue } from "../../../manager/util";

interface SeriesFeatureNumberProps {
  feature: InvestmentProductConfigurationFeature;
  investmentProductSeriesData: InvestmentProductSeriesDTO;
  selectedIndex: [number, number];
  isDisabled?: boolean;
}

export const SeriesFeatureNumber = ({
  feature,
  investmentProductSeriesData,
  selectedIndex = [0, 0],
  isDisabled = false,
}: SeriesFeatureNumberProps) => {
  const { control, setValue, getValues } = useFormContext<{
    investmentProductSeriesData: InvestmentProductSeriesDTO[];
  }>();
  const {
    investmentSeriesDataModifiedPath,
    sectionModifiedPath,
    featureModifiedPath,
    featureProductDataPath,
    featureProductHighlightTypePath,
    featureProductDataStatusPath,
    featureProductDataModifiedPath,
    investmentSeriesDataHighlightTypePath,
    featureProductDataPublishedPath,
    featureViewingGroupSelectionsPath,
    dropDownFeaturePath,
  } = getFeatureDataPath(selectedIndex, investmentProductSeriesData, feature);
  const { isShowOrangeDot, isShowBlueDot } = useInputDotIndicator<{
    investmentProductSeriesData: InvestmentProductSeriesDTO[];
  }>({
    getValues,
    control,
    paths: {
      highlightType: featureProductHighlightTypePath as SeriesPath,
      dataStatus: featureProductDataStatusPath as SeriesPath,
    },
  });

  const handleVisibilityChange = (
    newValue: ViewingGroupSelectionDTO[] | null,
  ) => {
    const isModified = !isEqual(feature.viewingGroupSelections, newValue);

    setValue(investmentSeriesDataModifiedPath, isModified);
    setValue(sectionModifiedPath, isModified);
    setValue(featureModifiedPath, isModified);
    setValue(featureProductDataModifiedPath, isModified);
  };
  const debounceVisibilityChange = debounce(
    handleVisibilityChange,
    EditProductDefaults.DebounceTime,
  );
  const handleInputChange = (inputValue: string) => {
    const publishedData = getValues(featureProductDataPublishedPath) ?? "";
    const highlightType = !isEqual(inputValue, publishedData)
      ? HighlightType.Edited
      : null;
    const dataStatus =
      isNil(inputValue) || isEmpty(inputValue)
        ? InvestmentDataStatus.MissingData
        : InvestmentDataStatus.Filled;
    const isModified = !isEqual(inputValue, feature.productData?.value);

    setValue(sectionModifiedPath, true);
    setValue(investmentSeriesDataModifiedPath, true);
    setValue(investmentSeriesDataHighlightTypePath, highlightType);
    setValue(featureModifiedPath, isModified);
    setValue(featureProductHighlightTypePath, highlightType);
    setValue(featureProductDataStatusPath, dataStatus);
    setValue(featureProductDataModifiedPath, isModified);
    setValue(featureProductDataPath, inputValue);
  };

  if (
    !investmentProductSeriesData ||
    getShowHideNoRoleFieldStatus(getValues, dropDownFeaturePath)
  ) {
    return <></>;
  }

  return (
    <>
      <SHDivider />
      <SHStack
        direction={"row"}
        justifyContent="space-between"
        gap={2}
        sx={{
          pt: "10px",
          pb: "5px",
          wordBreak: "break-word",
        }}
        key={featureProductDataPath}
        width={"100%"}
      >
        <SHStack width={"40%"} justifyContent="space-between" gap={2}>
          <SHStack>
            <SHLabel
              label={
                <CustomLabel
                  featureData={feature}
                  disableInformationButton
                  dotProps={{
                    orangeDot: isShowOrangeDot,
                    blueDot: isShowBlueDot,
                  }}
                />
              }
            />
            {feature?.description && (
              <SHTypography>{feature.description}</SHTypography>
            )}
          </SHStack>

          {feature.allowedVisibilitySetting && (
            <SHStack direction={"row"} alignItems={"center"}>
              <SHLabel
                label={
                  <SHTypography disabled={isDisabled} variant="subtitle2">
                    Visible to
                  </SHTypography>
                }
              />
              <Controller
                name={featureViewingGroupSelectionsPath as SeriesPath}
                control={control}
                render={({ field: { ref, ...other } }) => (
                  <SelectViewingGroups
                    sx={{
                      width: "70%",
                    }}
                    onChange={(value) => {
                      other.onChange(value);
                      debounceVisibilityChange(value);
                    }}
                    value={
                      (getValues(
                        featureViewingGroupSelectionsPath as SeriesPath,
                      ) as ViewingGroupSelectionDTO[] | null) || []
                    }
                    viewingGroupData={feature.viewingGroupSelections ?? []}
                    disabled={isDisabled}
                  />
                )}
              />
            </SHStack>
          )}
        </SHStack>
        <SHStack sx={{ width: "60%" }}>
          <Controller
            control={control}
            name={featureProductDataPath}
            render={({ field: { ref, ...other } }) => {
              return (
                <>
                  <SHNumericFieldV2
                    disabled={isDisabled}
                    thousandSeparator
                    sx={{
                      "& .MuiInput-root > input": {
                        padding: "10px 0px 8px 0px",
                      },
                      width: "520px",
                    }}
                    placeholder={"Please enter detail"}
                    value={toEditManagerNativeValue(
                      other.value,
                      FieldTypeId.Number,
                    )}
                    onValueChange={(values) => {
                      other.onChange(values.value);
                      handleInputChange(values.value);
                    }}
                  />
                </>
              );
            }}
          />
        </SHStack>
      </SHStack>
    </>
  );
};
